import { Callout, Button, MarkDown, Subheading, useToast } from "@wayflyer/flyui";
import * as React from "react";
import { useIntl } from "react-intl";
import { ConfirmModal } from "shared-ui";
import { styled } from "styled-components";

export interface NotesProps {
  initialText: string;
  saveHandler?: (text: string) => Promise<void>;
  onChangeHandler?: (text: string) => void;
}

const TextArea = styled.textarea`
  resize: none;
  width: 100%;
  height: inherit;
`;

const WrappedDiv = styled.div`
  overflow-y: auto;
  overflow-wrap: anywhere;
  height: inherit;
  width: inherit;
`;

const PreLineDiv = styled(WrappedDiv)`
  white-space: pre-line;
`;

const SaveButton = styled(Button)`
  min-width: unset;
  width: 100px;
`;

const FlexDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

/**
 * Notes component
 */
export const Notes = ({ initialText = "", onChangeHandler = () => {}, saveHandler }: NotesProps): UIElement => {
  const intl = useIntl();
  const toaster = useToast();

  const editable = saveHandler !== undefined;

  const [textContent, setTextContent] = React.useState(initialText);
  const [lastSavedText, setLastSavedText] = React.useState(initialText);
  const [saving, setSaving] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleChange = (event) => {
    onChangeHandler(event.target.value);
    setTextContent(event.target.value);
  };

  const onSaveConfirm = () => {
    setSaving(true);
    // Should always be the case if the button is there
    if (saveHandler) {
      saveHandler(textContent)
        .then(() => {
          toaster.positive(
            intl.formatMessage({
              id: "Notes tab save success message",
              defaultMessage: "The notes have been saved successfully",
            })
          );
          setSaving(false);
          setLastSavedText(textContent);
          setIsModalOpen(false);
        })
        .catch(() => {
          toaster.critical(
            intl.formatMessage({
              id: "Notes tab save error message",
              defaultMessage: "There was an error saving the notes. If the problem persists please contact support.",
            })
          );
          setSaving(false);
          setIsModalOpen(false);
        });
    }
  };

  const onSaveCancel = () => {
    setIsModalOpen(false);
  };

  const onSaveClick = () => {
    setIsModalOpen(true);
  };

  const hasUnsavedTextChanges = textContent !== lastSavedText;
  const warningSaveText = intl.formatMessage({
    id: "Notes tab chagnes visible to customer warning",
    defaultMessage: "Changes made here will be visible to the customer.",
  });

  if (editable) {
    return (
      <>
        <Subheading>
          {intl.formatMessage({ id: "Notes tab, customer View", defaultMessage: "Customer View" })}
        </Subheading>
        <WrappedDiv>
          <NotesMarkdown markdown={textContent} />
        </WrappedDiv>
        <br />
        <br />
        <Subheading>{intl.formatMessage({ id: "Notes tab editor title", defaultMessage: "Editor" })}</Subheading>
        <FlexDiv>
          <SaveButton variant="Primary" onClick={onSaveClick}>
            {intl.formatMessage({ id: "Notes tab save button message", defaultMessage: "Save" })}
          </SaveButton>
          {hasUnsavedTextChanges ? <Callout variant="warning" description={warningSaveText} /> : null}
        </FlexDiv>
        <ConfirmModal
          isOpen={isModalOpen}
          primaryAction={{
            onAction: onSaveConfirm,
            content: intl.formatMessage({ id: "Notes tab confirm label", defaultMessage: "Confirm" }),
            loading: saving,
          }}
          tertiaryAction={{ onAction: onSaveCancel }}
          title={intl.formatMessage({
            id: "Notes tab confirmation message",
            defaultMessage: "Are you sure you want to save this note?",
          })}
        >
          {warningSaveText}
        </ConfirmModal>
        <br />
        <TextArea data-testid="notes-textbox" name="textValue" onChange={handleChange} value={textContent} rows={25} />
        <br />
        <PreLineDiv>{MarkdownPrimer}</PreLineDiv>
      </>
    );
  } else {
    return (
      <WrappedDiv>
        <NotesMarkdown markdown={textContent} />
      </WrappedDiv>
    );
  }
};

const MarkdownPrimer = `
Headings: #, ## and ###
Bold: **bold text**
Italic: *italicized text*

Blockquote: > blockquote

Ordered List:
1. First item
2. Second item
3. Third item

Unordered List:
- First item
- Second item
- Third item

Code: \`code\`
Horizontal Rule: ---
Link: [title](https://www.example.com)
Image: ![alt text](https://freeimages.com/image.jpg)
`;

const NotesMarkdown = styled(MarkDown)`
  h1:not(:first-of-type),
  h2:not(:first-of-type),
  h4:not(:first-of-type),
  h5:not(:first-of-type) {
    margin-block-start: 1.5em;
    margin-block-end: 0;
  }
  p {
    margin-block-start: 0.5em;
    margin-block-end: 0.5em;
  }
`;
